import React, { Children, useContext, useEffect, useRef, useState } from 'react';
import cn from '@/utils/cn';
import { useDrop } from 'react-dnd';
import DndAnswerContext from 'components/common/Context';
import styles from './DropZone.module.scss';
import ItemTypes from '../../ItemTypes';
import { DraggableItemType } from '../../types';
import { positionedContainerStyle, sizedContainerStyle } from '../../utils';
import useDragAndDropStore from '../../useDragAndDropStore';

interface DropZoneType {
  children?: ReactChildren;
  dropZoneRef?: any;
  height?: number;
  id: string;
  isDropDisabled?: boolean;
  left: number;
  shape?: string;
  title: string;
  top: number;
  vocabId?: number;
  width?: number;
}

const DropZone = ({
  children,
  isDropDisabled = false,
  id,
  left,
  shape,
  title,
  top,
  width,
  height,
  dropZoneRef,
  vocabId,
}: DropZoneType) => {
  const label = `Drop zone ${id.toUpperCase()}`;
  const {
    answerChoices,
    answerChoiceDispatch,
    correctAnswers,
    hasTransparentDropzones,
    hasReusableAnswerChoices,
  } = useContext(DndAnswerContext);
  const isGloballyDragging = useDragAndDropStore(state => state.isGloballyDragging);
  const hasNoDroppedItems = !children || Children.count(children) === 0;

  const contentRef = useRef<HTMLDivElement>(null);
  const [shouldUseHorizontalLayout, setShouldUseHorizontalLayout] = useState(false);

  // Use horizontal layout if drop zone is wider than it is tall
  useEffect(() => {
    if (!children || Children.count(children) <= 1) {
      setShouldUseHorizontalLayout(false);
      return;
    }

    setShouldUseHorizontalLayout((width || 0) > (height || 0));
  }, [children, width, height]);

  const [{ highlighted }, drop] = useDrop(() => ({
    accept: ItemTypes.draggableChoice,
    canDrop: () => !isDropDisabled,
    collect: (monitor) => {
      const item: DraggableItemType = monitor.getItem();
      const canAcceptItem = item && !(answerChoices[id] || []).includes(item.id);

      return {
        canDrop: monitor.canDrop() && item && (hasReusableAnswerChoices || canAcceptItem),
        highlighted: monitor.isOver() && canAcceptItem,
        isOver: monitor.isOver(),
      };
    },
    drop: (item: DraggableItemType) => {
      answerChoiceDispatch({
        payload: {
          draggableChoiceId: item.id,
          dropZoneId: id,
          hasReusableAnswerChoices,
          sourceDropZoneId: item.sourceDropZoneId || null,
        },
        type: 'ADD',
      });
    },
  }), [children, isDropDisabled, answerChoices, hasReusableAnswerChoices]);

  const needsMoreAnswers = () => {
    const numChoices = answerChoices[id]?.length;
    const numCorrectAnswers = correctAnswers[id]?.length;

    return !!numChoices && !!numCorrectAnswers && (numChoices < numCorrectAnswers);
  };

  return (
    <div
      ref={drop}
      aria-label={label}
      className={styles.dropZoneWrapper}
      role="button"
      style={{
        ...positionedContainerStyle(left, top, width, (vocabId && shape === 'rectangle') ? 8 : height),
        zIndex: 2,
      }}
      tabIndex={0}
    >
      <div
        ref={dropZoneRef}
        className={cn(
          styles.dropZone, {
            [styles.moreAnswers]: needsMoreAnswers(),
            [styles.highlighted]: highlighted,
            [styles.transparentBackgroundDragging]: isGloballyDragging && hasTransparentDropzones,
            [styles.transparentBackgroundNotDragging]: !isGloballyDragging && hasTransparentDropzones,
            [styles.circle]: shape === 'circle',
            [styles.square]: shape !== 'circle',
          },
        )}
        style={sizedContainerStyle(false)}
      >
        <div className={styles.dropZoneContent}>
          {hasNoDroppedItems && (
            <div className={styles.label}>
              {title}
            </div>
          )}
          <div
            ref={contentRef}
            aria-label={`answer choices for Drop zone ${id}`}
            className={cn(styles.dropZoneContentChildren, {
              [styles.horizontal]: shouldUseHorizontalLayout,
            })}
            id={`test-${id}`}
            role="list"
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DropZone;
